import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useThunkDispatch as useDispatch, useAppSelector as useSelector } from 'hooks/reduxHooks';
import useForm from 'hooks/useLocalizedSimpleForm';
import { updatePushSimple } from 'store/push/push.thunk';
import { serializeActionlink, deserializeActionlink, generateActionlinkFieldKey } from 'helpers/push.helper';
import { PushValidators } from 'validators/ExperienceCanvas/push.validator';
import { PushFormClassPrefix as CLASS_PREFIX } from 'components/ExperienceCanvas/constants';
import { TFormProps, PushUniversalAction } from 'components/ExperienceCanvas/types';
import { FlightButton, FlightCheckbox } from '@flybits/design-system';
import ActionTypeSelector from './ActionTypeSelector';
import ActionLinkFieldControl from './ActionLinkFieldControl';
import ContentIdFieldRow from './ContentIdDropdown/ContentIdFieldRow';
import useContentIdSelector from './ContentIdDropdown/useContentIdSelector';

const ActionLinkForm = ({ refId, activeLang, defaultLang, stepIdx }: TFormProps) => {
  const dispatch = useDispatch();
  const [pushRefId, deserializedActionLink] = useSelector((state) => {
    const push = state.te.push.byRefId[refId];
    return [push?.refId, deserializeActionlink(push?.actionLinkScheme, activeLang)];
  });

  const { formData, updateField, toggleError } = useForm({
    data: {
      ...deserializedActionLink.formFields,
      bypassStrictValidation: { value: false },
    },
    validators: PushValidators,
    lang: activeLang,
    defaultLang,
  });

  const [selectedActionType, setSelectedActionType] = useState(deserializedActionLink.actionType);
  const [isActionSelectorOpen, setIsActionSelectorOpen] = useState(false);
  const [isContentIdSelectorOpen, setIsContentIdSelectorOpen] = useState(false);
  const [selectedContentDisplayName, setSelectedContentDisplayName] = useState('');
  const [lastSelectedContentId, setLastSelectedContentId] = useState('');

  const handleContentIdDropdownClose = useCallback(() => {
    setIsContentIdSelectorOpen(false);
  }, []);

  const contentIdSelector = useContentIdSelector({
    isOpen: isContentIdSelectorOpen,
    onClose: handleContentIdDropdownClose,
  });

  const handleChangeActionType = (actionType: PushUniversalAction) => {
    setSelectedActionType(actionType);
    setIsActionSelectorOpen(false);
    dispatch(
      updatePushSimple(
        {
          refId: pushRefId,
          fields: {
            actionLinkType: {
              displayName: actionType.name,
              val: 'UNIVERSAL_ACTION',
              validationPrefix: actionType.prefix,
            },
          },
        },
        stepIdx,
        'actionlink',
      ),
    );
  };

  const onChangeBypass = () => {
    const currentValue = formData.bypassStrictValidation.value;

    updateField('bypassStrictValidation', !currentValue);
    selectedActionType.fields.forEach((field, idx) => {
      const fieldKey = generateActionlinkFieldKey(selectedActionType, idx);
      toggleError(fieldKey);
    });
  };

  /**
   * TODO: if we add the actiontype dep into this effect and remove it from the change action handler,
   * the deserialization overwrites the acton type selection as deserialization relies soley on actionLinkScheme
   * as it is the source of truth for mobile as far as we know know.
   * Confirm with mobile if the actionLinkType is used at all, and look into wiping the user's scheme field
   * when type is changed.
   * */
  useEffect(() => {
    setSelectedActionType(deserializedActionLink.actionType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLang]);

  useEffect(() => {
    dispatch(
      updatePushSimple(
        {
          refId: pushRefId,
          fields: { actionLinkScheme: { [activeLang]: serializeActionlink(selectedActionType, formData) } },
        },
        stepIdx,
        'actionlink',
      ),
    );
  }, [activeLang, dispatch, formData, pushRefId, selectedActionType, stepIdx]);

  const isDetailsAction = selectedActionType.prefix === 'details:';

  const detailsContentIdKey = useMemo(() => {
    if (!isDetailsAction) return '';
    const contentIdFieldIdx = selectedActionType.fields.findIndex((field) => field.key === 'contentId');
    return generateActionlinkFieldKey(selectedActionType, contentIdFieldIdx >= 0 ? contentIdFieldIdx : 0);
  }, [isDetailsAction, selectedActionType]);

  const handleSelectContentId = useCallback(
    (contentId: string, contentName?: string) => {
      if (!detailsContentIdKey || !contentId) return;

      updateField(detailsContentIdKey, contentId);
      setLastSelectedContentId(contentId);
      setSelectedContentDisplayName(contentName || contentId);
      toggleError(detailsContentIdKey);
      contentIdSelector.close();
    },
    [contentIdSelector, detailsContentIdKey, toggleError, updateField],
  );

  useEffect(() => {
    if (!detailsContentIdKey) {
      setSelectedContentDisplayName('');
      setLastSelectedContentId('');
      return;
    }

    const contentId = formData[detailsContentIdKey]?.value?.trim() ?? '';
    if (!contentId) {
      setSelectedContentDisplayName('');
      setLastSelectedContentId('');
    }
  }, [detailsContentIdKey, formData]);

  const valueHeader = (
    <div
      className={`${CLASS_PREFIX}__row ${CLASS_PREFIX}__value-header ${
        isDetailsAction ? `${CLASS_PREFIX}__value-header--with-dropdown` : ''
      }`}
    >
      <div className={`${CLASS_PREFIX}__label`}>
        <p className={`${CLASS_PREFIX}__label-primary`}>Enter Universal Action&apos;s Value</p>
      </div>
      {isDetailsAction && (
        <FlightButton
          ariaLabel="select content id"
          label="Select Content ID"
          theme="link"
          onClick={() => setIsContentIdSelectorOpen((prev) => !prev)}
        />
      )}
    </div>
  );

  const actionFields = selectedActionType.fields.map((fieldMetadata, idx) => {
    const fieldKey = generateActionlinkFieldKey(selectedActionType, idx);
    const fieldKeyProp = `push-at-field-${selectedActionType.name}-${idx}`;

    if (isDetailsAction && fieldMetadata.key === 'contentId') {
      return (
        <ContentIdFieldRow
          key={fieldKeyProp}
          fieldKey={fieldKey}
          fieldMetadata={fieldMetadata}
          field={formData[fieldKey]}
          updateField={updateField}
          toggleError={toggleError}
          isDropdownOpen={isContentIdSelectorOpen}
          contentIdSelector={contentIdSelector}
          onSelectContentId={handleSelectContentId}
          selectedContentDisplayName={selectedContentDisplayName}
          lastSelectedContentId={lastSelectedContentId}
        />
      );
    }

    return (
      <div className={`${CLASS_PREFIX}__row`} key={fieldKeyProp}>
        <ActionLinkFieldControl
          fieldKey={fieldKey}
          fieldMetadata={fieldMetadata}
          field={formData[fieldKey]}
          updateField={updateField}
          toggleError={toggleError}
        />
      </div>
    );
  });

  return (
    <div className={`${CLASS_PREFIX}__container`}>
      <div className={`${CLASS_PREFIX}__row`}>
        <div className={`${CLASS_PREFIX}__label`}>
          <p className={`${CLASS_PREFIX}__label-primary`}>Select Universal Action</p>
        </div>
      </div>
      <ActionTypeSelector
        selectedActionType={selectedActionType}
        isOpen={isActionSelectorOpen}
        onOpenChange={setIsActionSelectorOpen}
        onSelect={handleChangeActionType}
      />
      {isDetailsAction ? (
        <div ref={contentIdSelector.scopeRef} className={`${CLASS_PREFIX}__content-id-dropdown-scope`}>
          {valueHeader}
          {actionFields}
        </div>
      ) : (
        <>
          {valueHeader}
          {actionFields}
        </>
      )}
      <div className={`${CLASS_PREFIX}__actionlink__preview`}>
        {`Action Preview: ${serializeActionlink(selectedActionType, formData)}`}
      </div>
      <div className={`${CLASS_PREFIX}__row`}>
        <div className={`${CLASS_PREFIX}__label`}>
          <p className={`${CLASS_PREFIX}__label-primary`}>Allow custom value for {selectedActionType.name}</p>
        </div>
        <FlightCheckbox
          className={`${CLASS_PREFIX}__right`}
          checkState={formData.bypassStrictValidation.value ? 'SELECTED' : 'UNSELECTED'}
          onSelect={onChangeBypass}
        />
      </div>
    </div>
  );
};

export default ActionLinkForm;
